//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Switch } from '../../ui/switch';
import { Label } from '../../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import TaskCard from './TaskCard';
import { cn } from '../../../lib/utils';
import { useTheme } from '../../../ThemeContext';
import { TaskMeta, loadTaskDefinitionsFromMock } from './loadTasks';
import AiDataService from '../../../utils/AiDataService';
import { useUserAuth } from '../../../UserAuth';
import Loading from '../../loading/loading';

interface TasksTabProps {
  selectedLoopId?: string;
  selectedLoopName?: string | null;
  teamspaceId?: string;
  teamspaceName?: string;
  onSendToChat?: (taskId: string, text: string) => void;
  onTaskSelected?: (
    taskId: string,
    taskName: string,
    conversation_starters: NonNullable<TaskMeta['conversation_starters']>,
    taskIcon?: TaskMeta['icon'],
    taskDescription?: string,
  ) => void;
  isBusy?: boolean;
  disabledTooltip?: string;
}

const TasksTab: React.FC<TasksTabProps> = ({
  selectedLoopId,
  selectedLoopName,
  teamspaceId,
  teamspaceName,
  onTaskSelected,
  isBusy = false,
  disabledTooltip,
}) => {
  const { t } = useTranslation();
  const { darkMode } = useTheme();
  const [showOnlyFav, setShowOnlyFav] = React.useState(false);
  const [tasks, setTasks] = React.useState<TaskMeta[]>([]);
  const [selectedCategory, setSelectedCategory] = React.useState<string>('');
  const [allTasks, setAllTasks] = React.useState<TaskMeta[]>([]);
  const [favoriteIds, setFavoriteIds] = React.useState<Set<string>>(new Set());
  const { user, isAuthenticated } = useUserAuth();
  const [isLoading, setIsLoading] = React.useState(true);
  // Cache key memoized per teamspace + loop to avoid re-fetching within session
  const cacheKey = React.useMemo(() => {
    if (teamspaceId && selectedLoopId) {
      return `ai:tasks:v1:${teamspaceId}:${selectedLoopId}`;
    }
    // Fallback key for dev or when IDs are not available
    return process.env.NODE_ENV === 'development'
      ? `ai:tasks:v1:dev:${selectedLoopId || 'default'}`
      : null;
  }, [teamspaceId, selectedLoopId]);

  const aiService = React.useMemo(
    () =>
      new AiDataService(
        () => user,
        () => isAuthenticated,
      ),
    [user, isAuthenticated],
  );

  React.useEffect(() => {
    let active = true;

    (async () => {
      const isDev = process.env.NODE_ENV === 'development';
      const shouldFetch = isDev || (teamspaceId && selectedLoopId);

      // First, attempt to load from sessionStorage cache for this loop + teamspace
      if (cacheKey) {
        try {
          const cached = sessionStorage.getItem(cacheKey);
          if (cached) {
            const parsed = JSON.parse(cached);
            if (Array.isArray(parsed)) {
              // Minimal shape validation
              const valid = parsed.every(
                (it: any) =>
                  it &&
                  typeof it.id === 'string' &&
                  typeof it.name === 'string' &&
                  typeof it.description === 'string',
              );
              if (valid) {
                if (active) {
                  const parsedTasks = parsed as TaskMeta[];
                  setTasks(parsedTasks);
                  setAllTasks(parsedTasks);
                  setIsLoading(false);
                }
                // Skip network fetch if cache is present
                return;
              }
            }
          }
        } catch (_) {
          // Ignore cache parsing errors and proceed to fetch
        }
      }

      if (shouldFetch) {
        if (active) {
          setIsLoading(true);
        }
      } else {
        if (active) {
          setIsLoading(false);
        }
      }

      if (isDev) {
        try {
          const mockDefs = await loadTaskDefinitionsFromMock();
          if (active) {
            setTasks(mockDefs);
            setAllTasks(mockDefs);
            // Cache dev tasks to avoid repeated mock loads while module is open
            if (cacheKey) {
              try {
                sessionStorage.setItem(cacheKey, JSON.stringify(mockDefs));
              } catch (_) {
                // Swallow storage errors silently
              }
            }
          }
        } finally {
          if (active) {
            setIsLoading(false);
          }
        }
        return;
      }

      // Production: use authenticated service API getLoopTasks
      try {
        if (teamspaceId && selectedLoopId) {
          const resp: any = await aiService.getLoopTasks(
            teamspaceId,
            selectedLoopId,
          );
          let items: Array<any> = [];
          let categories: Array<{ name: string; tasks: any[] }> = [];
          let capabilities: Array<{ name: string; tasks: any[] }> = [];

          // Support both 'categories' and 'capabilities' for backward compatibility
          if (Array.isArray(resp?.categories)) {
            categories = resp.categories;
            for (const group of categories) {
              const catName =
                typeof group?.name === 'string' ? group.name.trim() : '';
              const groupTasks: Array<any> = Array.isArray(group?.tasks)
                ? group.tasks
                : [];
              for (const task of groupTasks) {
                // Attach category to each task for filtering
                items.push({ ...task, category: catName });
              }
            }
          } else if (Array.isArray(resp?.capabilities)) {
            capabilities = resp.capabilities;
            for (const group of capabilities) {
              const capName =
                typeof group?.name === 'string' ? group.name.trim() : '';
              const groupTasks: Array<any> = Array.isArray(group?.tasks)
                ? group.tasks
                : [];
              for (const task of groupTasks) {
                // Attach capability as category for filtering
                items.push({ ...task, category: capName });
              }
            }
          } else if (Array.isArray(resp)) {
            items = resp;
          } else if (Array.isArray(resp?.tasks)) {
            items = resp.tasks;
          } else if (Array.isArray(resp?.items)) {
            items = resp.items;
          }

          const mapped: TaskMeta[] = [];
          for (const item of items) {
            try {
              const providedName =
                typeof item?.name === 'string' ? (item.name as string) : '';
              const providedDescription =
                typeof item?.description === 'string'
                  ? (item.description as string)
                  : '';
              const prompt =
                typeof item?.prompt === 'string'
                  ? (item.prompt as string)
                  : undefined;
              const providedId =
                typeof item?.id === 'string' ? (item.id as string) : undefined;
              const idSource =
                providedId && providedId.trim().length > 0
                  ? providedId
                  : providedName || 'task';
              const id = String(idSource);

              let icon: TaskMeta['icon'] | undefined;
              const rawIcon =
                (item && item.icon) ||
                (Array.isArray(item?.icons) && item.icons.length > 0
                  ? item.icons[0]
                  : undefined);
              if (typeof rawIcon === 'string') {
                const s = rawIcon.trim();
                if (/^(data:|https?:\/\/)/i.test(s)) {
                  icon = { data: s };
                } else if (s.length <= 4 && !/\s/.test(s)) {
                  icon = { emoji: s };
                } else {
                  icon = { name: s };
                }
              } else if (rawIcon && typeof rawIcon === 'object') {
                icon = {
                  name: rawIcon?.name,
                  data: rawIcon?.data || rawIcon?.url || rawIcon?.src,
                  emoji: rawIcon?.emoji,
                  color: rawIcon?.color,
                };
              }

              const name = providedName || id;
              const description = providedDescription || '';

              // Extract capabilities from metadata array or category property
              let capabilities: string[] | undefined;
              const metadataCategories = item?.metadata?.categories;
              if (Array.isArray(metadataCategories)) {
                capabilities = metadataCategories.filter(
                  (c: any) => typeof c === 'string' && c.trim().length > 0,
                );
              }
              if (!capabilities || capabilities.length === 0) {
                // Fallback to single category if metadata doesn't have categories array
                let category: string | undefined;
                if (typeof item?.category === 'string') {
                  const c = item.category.trim();
                  if (c) category = c;
                }
                capabilities = category ? [category] : undefined;
              }

              // Optional conversation starters from metadata (new key), fallback to legacy 'starters'
              let starters: TaskMeta['conversation_starters'];
              const rawConvStarters =
                item?.metadata?.conversation_starters ??
                item?.metadata?.starters ??
                item?.conversation_starters;
              if (Array.isArray(rawConvStarters)) {
                starters = rawConvStarters
                  .map((s: any) => {
                    const label =
                      typeof s?.label === 'string' ? s.label.trim() : '';
                    const sprompt =
                      typeof s?.prompt === 'string' ? s.prompt.trim() : '';
                    if (!label || !sprompt) return undefined;
                    return { label, prompt: sprompt };
                  })
                  .filter(Boolean) as TaskMeta['conversation_starters'];
                if (starters && starters.length === 0) starters = undefined;
              }

              if (name && description) {
                mapped.push({
                  id,
                  name,
                  description,
                  icon,
                  prompt,
                  capability: capabilities?.[0], // for backward compatibility
                  capabilities, // use all capabilities from metadata
                  conversation_starters: starters,
                });
              }
            } catch (err) {
              if (process.env.NODE_ENV !== 'production') {
                console.warn(
                  '[TasksTab] Skipping invalid task item',
                  err,
                  item,
                );
              }
            }
          }

          if (mapped.length > 0) {
            // Deduplicate tasks by name, merging all capabilities from all occurrences
            const byName = new Map<string, TaskMeta>();
            for (const task of mapped) {
              if (byName.has(task.name)) {
                // Merge capabilities from this occurrence with existing task
                const existing = byName.get(task.name)!;
                const existingCaps = new Set(existing.capabilities || []);
                if (task.capabilities) {
                  task.capabilities.forEach((cap) => existingCaps.add(cap));
                }
                existing.capabilities = Array.from(existingCaps);
              } else {
                byName.set(task.name, task);
              }
            }
            const uniqueTasks = Array.from(byName.values());
            const sorted = uniqueTasks.sort((a, b) =>
              a.name.localeCompare(b.name),
            );
            if (active) {
              setTasks(sorted);
              setAllTasks(sorted);
            }
            // Persist to sessionStorage so re-opening Tasks tab won't refetch
            if (cacheKey) {
              try {
                sessionStorage.setItem(cacheKey, JSON.stringify(sorted));
              } catch (_) {
                // Ignore storage errors
              }
            }
            return;
          }
        }
      } catch (err) {
        if (process.env.NODE_ENV !== 'production') {
          console.error('[TasksTab] Failed to load loop tasks', err);
        }
      } finally {
        if (active) {
          setIsLoading(false);
        }
      }

      // No local markdown or legacy JSON in production; show empty
      if (active) {
        setTasks([]);
        setAllTasks([]);
      }
    })();

    return () => {
      active = false;
    };
  }, [selectedLoopId, selectedLoopName, teamspaceId, teamspaceName, cacheKey]);
  const OTHER_CATEGORY_LABEL = 'Others';
  const categories = React.useMemo(() => {
    const labels = new Set<string>();
    let hasUntypedTasks = false;

    allTasks.forEach((t) => {
      let hasExplicit = false;
      if (Array.isArray(t.capabilities)) {
        for (const cat of t.capabilities) {
          const c = cat?.trim();
          if (c) {
            labels.add(c);
            hasExplicit = true;
          }
        }
      }
      const single = t.capability?.trim();
      if (single) {
        labels.add(single);
        hasExplicit = true;
      }
      if (!hasExplicit) {
        hasUntypedTasks = true;
      }
    });
    const list = Array.from(labels).sort((a, b) => a.localeCompare(b));
    if (hasUntypedTasks && !list.includes(OTHER_CATEGORY_LABEL)) {
      list.push(OTHER_CATEGORY_LABEL);
    }
    return list;
  }, [allTasks]);

  const visibleTasks = React.useMemo(
    () =>
      tasks.filter((task) => {
        if (!selectedCategory) {
          return true;
        }
        // If "Others" is selected, show tasks that have no explicit category
        // or that explicitly list "Others" in their category/capabilities.
        if (selectedCategory === OTHER_CATEGORY_LABEL) {
          const hasExplicit =
            (Array.isArray(task.capabilities) &&
              task.capabilities.some((c) => c?.trim())) ||
            !!task.capability?.trim();
          if (!hasExplicit) return true;
          if (Array.isArray(task.capabilities)) {
            return task.capabilities.includes(OTHER_CATEGORY_LABEL);
          }
          return task.capability === OTHER_CATEGORY_LABEL;
        }
        if (Array.isArray(task.capabilities)) {
          if (task.capabilities.some((c) => c?.trim() === selectedCategory)) {
            return true;
          }
        }
        if (task.capability?.trim() === selectedCategory) {
          return true;
        }
        return false;
      }),
    [tasks, selectedCategory],
  );

  // Fetch favorites on initial load (or when loop/teamspace changes)
  React.useEffect(() => {
    let cancelled = false;
    if (!teamspaceId || !selectedLoopId) {
      setFavoriteIds(new Set());
      return () => {
        cancelled = true;
      };
    }

    (async () => {
      try {
        const resp: any = await aiService.getFavoriteTasks(
          teamspaceId,
          selectedLoopId,
        );
        const toId = (x: any) =>
          typeof x === 'string'
            ? x
            : typeof x?.task_id === 'string'
              ? x.task_id
              : String(x);
        const favIds: string[] = Array.isArray(resp)
          ? resp.map(toId)
          : Array.isArray(resp?.items)
            ? resp.items.map(toId)
            : Array.isArray(resp?.favorites)
              ? resp.favorites.map(toId)
              : [];
        if (!cancelled) setFavoriteIds(new Set(favIds.map(String)));
      } catch (err) {}
    })();

    return () => {
      cancelled = true;
    };
  }, [teamspaceId, selectedLoopId]);

  // When toggling favorites, filter using favoriteIds; fetch if missing
  React.useEffect(() => {
    let cancelled = false;
    const applyAll = () => {
      if (!cancelled) setTasks(allTasks);
    };

    if (!showOnlyFav) {
      applyAll();
      return () => {
        cancelled = true;
      };
    }

    // Require identifiers to fetch favorites
    if (!teamspaceId || !selectedLoopId) {
      if (!cancelled) setTasks([]);
      return () => {
        cancelled = true;
      };
    }

    const filterWithFavs = (favSet: Set<string>) => {
      const filtered = allTasks.filter((t) => favSet.has(String(t.id)));
      setTasks(filtered);
    };

    if (favoriteIds.size > 0) {
      filterWithFavs(favoriteIds);
    } else {
      (async () => {
        try {
          const resp: any = await aiService.getFavoriteTasks(
            teamspaceId,
            selectedLoopId,
          );
          const toId = (x: any) =>
            typeof x === 'string'
              ? x
              : typeof x?.task_id === 'string'
                ? x.task_id
                : String(x);
          const favIds: string[] = Array.isArray(resp)
            ? resp.map(toId)
            : Array.isArray(resp?.items)
              ? resp.items.map(toId)
              : Array.isArray(resp?.favorites)
                ? resp.favorites.map(toId)
                : [];
          if (cancelled) return;
          const favSet = new Set(favIds.map(String));
          setFavoriteIds(favSet);
          filterWithFavs(favSet);
        } catch (err) {}
      })();
    }

    return () => {
      cancelled = true;
    };
  }, [showOnlyFav, teamspaceId, selectedLoopId, allTasks, favoriteIds]);

  if (isLoading) {
    return <Loading />;
  }

  return (
    <div className="p-3 px-4" data-testid="id-ai-tasks-tab">
      <span
        className={cn(
          'font-[Roboto] text-base font-semibold text-[#354958]',
          darkMode && 'text-[#e0e0e0]',
        )}
      >
        {t('tasks-heading')}
      </span>
      <span
        className={cn(
          'mt-2 block font-[Roboto] text-xs leading-5 text-black',
          darkMode && 'text-[#b0b0b0]',
        )}
      >
        {t('tasks-line')}
      </span>
      <div className="-ml-[15px] mr-[10px] mt-4 flex items-center justify-between gap-3">
        <div className="ml-3 mt-2 flex items-center gap-2.5 p-0.5">
          <Switch
            id="tasks-show-only-favorites"
            checked={showOnlyFav}
            onCheckedChange={setShowOnlyFav}
            aria-label={t('show-only-favorites')}
          />
          <Label
            htmlFor="tasks-show-only-favorites"
            className={cn(
              'cursor-pointer font-[Inter] text-xs font-normal text-black/85',
              darkMode && 'text-[#b0b0b0]',
            )}
          >
            {t('show-only-favorites')}
          </Label>
        </div>

        <div className="flex items-center gap-2">
          <span
            className={cn(
              'font-[Roboto] text-sm text-[#717182]',
              darkMode && 'text-[#b0b0b0]',
            )}
          >
            {t('categories')}
          </span>
          <Select
            value={selectedCategory || 'all'}
            onValueChange={(v) => setSelectedCategory(v === 'all' ? '' : v)}
          >
            <SelectTrigger
              aria-label={t('filter-by-category')}
              className={cn(
                'h-8 min-w-[150px] font-[Roboto] text-sm',
                darkMode &&
                  'border-[#4a4a4a] text-[#e0e0e0] hover:border-[#5a5a5a]',
              )}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent
              className={cn(
                'max-h-[400px] w-[220px]',
                darkMode && 'bg-[#2d2d2d] text-[#e0e0e0]',
              )}
            >
              <SelectItem value="all">{t('all-categories')}</SelectItem>
              {categories.map((cat) => (
                <SelectItem key={cat} value={cat}>
                  {t(cat, cat)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="mt-5 grid max-h-[60vh] grid-cols-1 gap-4 overflow-y-auto pr-2">
        {visibleTasks.length === 0 && (
          <span className="py-8 text-center text-sm text-[#666]">
            {showOnlyFav
              ? t(
                  'no-favorite-tasks',
                  'No favorited tasks match the current filter.',
                )
              : t(
                  'no-tasks-in-category',
                  'No tasks found for the selected category.',
                )}
          </span>
        )}
        {visibleTasks.map((task) => (
          <TaskCard
            key={task.id}
            id={task.id}
            title={task.name}
            description={task.description}
            icon={task.icon}
            prompt={task.prompt}
            teamspaceId={teamspaceId}
            loopId={selectedLoopId}
            isFavorite={favoriteIds.has(String(task.id))}
            onFavoriteChange={(id, isFav) => {
              setFavoriteIds((prev) => {
                const next = new Set(prev);
                const sid = String(id);
                if (isFav) next.add(sid);
                else next.delete(sid);
                // If currently filtering favorites, keep list in sync
                if (showOnlyFav) {
                  const filtered = allTasks.filter((t) =>
                    next.has(String(t.id)),
                  );
                  setTasks(filtered);
                }
                return next;
              });
            }}
            disabled={isBusy}
            disabledTooltip={disabledTooltip}
            onTitleClick={(id, title) => {
              if (isBusy) return;
              const starters =
                task.conversation_starters &&
                task.conversation_starters.length > 0
                  ? task.conversation_starters
                  : [{ label: t('task-run-it'), prompt: t('task-run-it') }];

              onTaskSelected?.(
                id,
                title,
                starters as NonNullable<TaskMeta['conversation_starters']>,
                task.icon,
                task.description,
              );
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default TasksTab;
